/**
 * Where the run's steps sit in the conversation (agion/Agion#2827): each step
 * goes before the first message that came after the step started, as the
 * Mission Designer's run chat orders its steps among its messages. The
 * messages keep their own order; only the steps are placed. Neutral: it
 * imports nothing from src/next.
 */
import type { ConversationRunStep } from './chatSurface'

export type ConversationFeedItem<M> =
  | { kind: 'message'; message: M }
  | { kind: 'step'; step: ConversationRunStep }

export function placeRunSteps<M extends { timestamp: number }>(
  messages: readonly M[],
  steps: readonly ConversationRunStep[],
): ConversationFeedItem<M>[] {
  if (steps.length === 0) return messages.map((message) => ({ kind: 'message', message }))
  const pending = [...steps].sort((a, b) => a.startedAt - b.startedAt)
  const feed: ConversationFeedItem<M>[] = []
  let next = 0
  for (const message of messages) {
    // A step that started at the same moment as a message reads first, as it
    // does in the designer's thread.
    while (next < pending.length && pending[next]!.startedAt <= message.timestamp) {
      feed.push({ kind: 'step', step: pending[next]! })
      next++
    }
    feed.push({ kind: 'message', message })
  }
  for (; next < pending.length; next++) feed.push({ kind: 'step', step: pending[next]! })
  return feed
}
